import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonItem,
  IonCard,
  IonLabel,
  IonLoading,
  IonToast,
  IonToggle,
  IonCardHeader,
  IonCardContent,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { getPatientAllergiesSelector } from '../../selectors'
import { useTranslation } from 'react-i18next'
import { saveAllergies } from '../../store/patientAllergies.actions'
import { getPatientAllergyOptionsSelector } from '../../../options/selectors'
import { Virtuoso } from 'react-virtuoso'
import CustomInput from '../../../../components/custominput/CustomInput.component'
import { IAllergyOptions, IAllergyOptionsList } from '../../store/patientAllergies.types'
import { IOption } from '../../../../common/common.types'
import { getDisabledAllergyList, isAnyNoKnowDrugAllergySelected } from '../../util/getDisabledAllergylist.util'
import { ToastColor } from '../../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../../components/toast/toastIcon.util'

interface AllergiesSearchListProps {
  searchValue: string
  allergyOptions: IAllergyOptionsList
  setSearchResults: React.Dispatch<React.SetStateAction<IAllergyOptionsList>>
  setSearchValue: React.Dispatch<React.SetStateAction<string>>
}

const AllergiesSearchList = (props: AllergiesSearchListProps) => {
  const { searchValue, allergyOptions, setSearchResults, setSearchValue } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const patientAllergiesState = useSelector(getPatientAllergiesSelector)
  const patientAllergiesOptions = useSelector(getPatientAllergyOptionsSelector)
  const { allergies, isLoading } = patientAllergiesState
  const { transformedChunkedPatientAllergyOptions, patientAllergies } = patientAllergiesOptions
  const [allergyType, setAllergyType] = useState('medication')
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [isWarning, setIsWarning] = useState(false)

  const onTogglePatientAllergies = (e: BaseSyntheticEvent, option: any) => {
    e.stopPropagation()

    if (e.target.checked !== undefined) {
      if (e.target.checked) {
        dispatch(saveAllergies([...allergies, option]))
      } else {
        option.selected = false
        if (allergies.filter((o) => o.id === option.id).length > 0) {
          allergies.splice(
            allergies.findIndex((o) => o.id === option.id),
            1
          )
        }
        dispatch(saveAllergies(allergies))
      }
    }
  }

  const pushData = () => {
    const chunk = patientAllergies.slice(allergyOptions.length, allergyOptions.length + 20)
    chunk.map((option: IAllergyOptions) => {
      const item: IAllergyOptions | undefined = allergies.find((item: IOption) => item.name === option.name)
      if (item) {
        option.selected = item.selected
      } else {
        option.selected = false
      }
      return option
    })
    setSearchResults([...allergyOptions, ...chunk])
  }

  const loadData = () => {
    if (searchValue === '') {
      pushData()
    }
  }

  const save = () => {
    const allergyCheck = allergies.filter((allergy) => allergy.name?.toLowerCase() === searchValue.toLowerCase())

    if (allergyCheck.length === 0) {
      const customAllergy = {
        id: 0,
        name: searchValue,
        nemsisCode: null,
        sourceDataset: null,
        allergyType: allergyType,
        selected: true,
      }
      dispatch(saveAllergies([...allergies, customAllergy]))
      setToastMessage('Allergy successfully added')
      setSearchValue('')
      setIsWarning(false)
      setShowToast(true)
    } else {
      setToastMessage('This Allergy was already entered')
      setIsWarning(true)
      setShowToast(true)
    }
  }

  const cancel = () => {
    setSearchValue('')
  }

  useEffect(() => {
    if (showToast) {
      setTimeout(() => {
        setShowToast(false)
      }, 2000)
    }
  }, [showToast])

  return (
    <>
      {showToast && (
        <IonToast
          position='top'
          isOpen={showToast}
          message={toastMessage}
          duration={2000}
          {...getToastProps(isWarning ? ToastColor.Warning : ToastColor.Success)}
          onDidDismiss={() => setShowToast(false)}
        />
      )}
      {!allergyOptions.length && (
        <>
          <IonCard className='ion-no-margin'>
            <IonCardHeader>
              <IonItem key={'no_options_found'}>
                <IonLabel>
                  {t('common.NoSearchResults')}
                  {`"${searchValue}"`}
                </IonLabel>
              </IonItem>
            </IonCardHeader>
            <IonCardContent>
              <CustomInput
                label='Allergy Type'
                heading='Manually add as new allergy'
                type={allergyType}
                setType={setAllergyType}
                cancel={cancel}
                save={save}
                firstSegmentValidation={() => isAnyNoKnowDrugAllergySelected(allergies)}
                saveButtonValidation={(currentActiveButton: string | undefined) => {
                  return currentActiveButton === 'medication' ? isAnyNoKnowDrugAllergySelected(allergies) : false
                }}
              />
            </IonCardContent>
          </IonCard>
        </>
      )}
      {transformedChunkedPatientAllergyOptions && allergyOptions && (
        <Virtuoso
          style={{ height: window.innerHeight }}
          data={allergyOptions}
          endReached={loadData}
          overscan={10}
          increaseViewportBy={{ top: 2000, bottom: 2000 }}
          itemContent={(index, option: IAllergyOptions) => {
            const isSelected = !!allergies.find((allergy) => allergy.id === option.id)

            return (
              <div style={{ minHeight: '44px' }} className='webkit-line-clamp-2'>
                <IonItem
                  data-testid={`AllergyOption_${option.id}`}
                  key={`Option_${option.id}`}
                  className='allergy-toggle'
                  data-skip-auto-enhance>
                  <IonLabel>{option.name}</IonLabel>
                  <IonToggle
                    slot='start'
                    name={option.name}
                    value={`${option.id}`}
                    checked={isSelected}
                    disabled={getDisabledAllergyList(allergies, option)}
                    onClick={(e) => onTogglePatientAllergies(e, option)}
                  />
                </IonItem>
              </div>
            )
          }}
        />
      )}
      {isLoading && <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} />}
    </>
  )
}

export default AllergiesSearchList
